import { Raycaster, Matrix4, Matrix3, Vector3, Color } from "three";
import { System } from "ecsy";
import {
  Object3DCmp,
  UIIntersectableCmp,
  ObjectIntersectableCmp,
} from "./Components.js";

// 公共工具方法
const RayUtils = {
  createRaycaster: () => new Raycaster(),
  getControllerDirection: (controller, tempMatrix) => {
    controller.updateMatrixWorld();
    tempMatrix.identity().extractRotation(controller.matrixWorld.clone());
    return new Vector3(0, 0, -1).applyMatrix4(tempMatrix);
  },
};

/**
 * VR中射线击中UI对象时让cursor跟随射线 只有主控制器会响应
 */
export class UIHandRaySystem extends System {
  init(attributes) {
    this.handControllors = [];
    this.tempMatrix = new Matrix4();
    if (attributes.controller1) {
      this.handControllors.push(attributes.controller1);
    }
    if (attributes.controller2) {
      this.handControllors.push(attributes.controller2);
    }

    this.dragSlider = false; // 是否正在拖拽滑动条，如果是则不对物体检测

    this._initRaycasters();

    console.log("初始化UIHandRaySystem");
  }

  // 公共初始化方法
  _initRaycasters() {
    this.raycasters = new Map();
    this.handControllors.forEach((controller) => {
      this.raycasters.set(controller, RayUtils.createRaycaster());
    });
  }

  addController(controller) {
    this.handControllors.push(controller);

    this.raycasters.set(controller, RayUtils.createRaycaster());
  }

  /**
   * 设置拖拽滑动条状态
   * @param {*} state
   */
  setDragState(state) {
    this.dragSlider = state;
  }

  execute(/* delta, time */) {
    this.handControllors.forEach((controller) => {
      if (controller && /*controller._isMain &&*/ !controller.disableRay) {
        const intersects = this._findBestIntersection(controller);
        this._handleIntersectionResult(intersects, controller);
      }
    });
  }

  // 射线检测优化方法
  _findBestIntersection(controller) {
    const raycaster = this.raycasters.get(controller);
    const direction = RayUtils.getControllerDirection(
      controller,
      this.tempMatrix
    );

    let worldPos = null;

    raycaster.set(controller.getWorldPosition(new Vector3()), direction);

    return this.queries.intersectable.results.reduce((best, entity) => {
      // 增加组件类型验证，避免模型对象也被检测到（Ecsy插件的bug）
      if (!entity.hasComponent(UIIntersectableCmp)) return best;

      const object = entity.getComponent(Object3DCmp)?.object;
      if (!object) return best;
      if (!this._isValidObject(object)) return best;

      // 检测当前位置是否有效
      if (worldPos && object.onEcsycheckSelectable) {
        if (!object.onEcsycheckSelectable(worldPos.clone())) return best;
      }

      const intersect = raycaster.intersectObject(object, false)[0];

      // 获取当前光标所在位置的世界坐标
      if (intersect && !worldPos) {
        worldPos = intersect.point.clone();
        if (
          object.onEcsycheckSelectable &&
          !object.onEcsycheckSelectable(worldPos.clone())
        ) {
          return best;
        }
      }

      return this._compareIntersections(best, intersect);
    }, null);
  }

  _compareIntersections(currentBest, newIntersect) {
    if (!newIntersect) return currentBest;
    if (!currentBest) return newIntersect;

    // 优先级比较逻辑
    const currentPriority = currentBest.object._priority || 0;
    const newPriority = newIntersect.object._priority || 0;

    if (newPriority < currentPriority) {
      return newIntersect;
    } else if (newPriority === currentPriority) {
      // 相同优先级时选择距离更近的
      return newIntersect.distance < currentBest.distance
        ? newIntersect
        : currentBest;
    }
    return currentBest;
  }

  // 对象有效性检查
  _isValidObject(object) {
    return (
      object?.visible &&
      object?.material?.visible &&
      object?.parent?.visible &&
      !object._dontCheckEcsy
    );
  }

  _handleIntersectionResult(intersect, controller) {
    if (!intersect) {
      this._cancelSelect(controller);

      if (controller._cursor) {
        if (controller._cursor._parent) controller._cursor.removeFromParent();
      }
      if (controller._floorCursor) {
        controller._floorCursor.removeFromParent();
      }
    } else {
      this._select(intersect.object, controller);
      // 设置3D光标
      if (controller._cursor) {
        if (controller._cursor._parent)
          controller._cursor._parent.add(controller._cursor);

        // 延长光标射线
        let normal = intersect.face.normal;
        let normalMatrix = new Matrix3();
        let worldNormal = new Vector3();
        normalMatrix.getNormalMatrix(intersect.object.matrixWorld);
        worldNormal.copy(normal).applyMatrix3(normalMatrix).normalize();

        let pos = intersect.point.clone();

        controller.updateMatrixWorld();
        let localPos = pos
          .clone()
          .applyMatrix4(controller.matrixWorld.clone().invert());
        let dis = localPos.distanceTo(new Vector3());
        controller.children[0].scale.z = dis;

        // 设置光标位置
        controller._cursor._parent.add(controller._cursor);
        controller._cursor.position.copy(pos);
        controller._cursor.lookAt(pos.clone().add(worldNormal));
        //controller._cursor.position.add(worldNormal.multiplyScalar(0.02));
      }

      // 隐藏地面光标
      if (controller._floorCursor) {
        controller._floorCursor.removeFromParent();
      }
    }
  }

  _select(object, controller) {
    if (this.dragSlider) return;

    if (controller.lastUI && object === controller.lastUI) return;

    this._cancelSelect(controller);

    controller._hoverType = "UI";

    if (controller._isMain && object.onEcsyHoverEnter) {
      object.onEcsyHoverEnter(object);
    }

    controller.lastUI = object;
  }

  _cancelSelect(controller) {
    if (this.dragSlider) return;
    if (controller._cursor) controller._cursor.removeFromParent();
    if (controller.lastUI && controller._isMain) {
      if (controller.lastUI.onEcsyHoverExit) {
        controller.lastUI.onEcsyHoverExit(controller.lastUI);
      }
    }
    controller._hoverType = null;
    controller.lastUI = null;
    controller.children[0].scale.z = 1.5;
  }
}

UIHandRaySystem.queries = {
  intersectable: {
    components: [UIIntersectableCmp],
  },
};

/**
 * VR中射线击中模型对象时让cursor跟随射线
 */
export class ObjectHandRaySystem extends System {
  init(attributes) {
    this.handControllors = [];

    this.tempMatrix = new Matrix4();

    this.lastObj = [];

    if (attributes.controller1) {
      this.handControllors.push(attributes.controller1);
    }
    if (attributes.controller2) {
      this.handControllors.push(attributes.controller2);
    }

    this.checkFloor = true; // 是否检测地面

    this._initRaycasters();
    console.log("初始化ObjectHandRaySystem");
  }

  // 公共初始化方法
  _initRaycasters() {
    this.raycasters = new Map();
    this.handControllors.forEach((controller) => {
      this.raycasters.set(controller, RayUtils.createRaycaster());
    });
  }

  addController(controller) {
    this.handControllors.push(controller);

    this.raycasters.set(controller, RayUtils.createRaycaster());
  }

  /**
   * 是否检测地面
   * @param {*} state
   */
  setCheckFloorState(state) {
    this.checkFloor = state;
  }

  execute(/* delta, time */) {
    for (var i = 0; i < this.handControllors.length; i++) {
      const controller = this.handControllors[i];
      // 禁止光标射线检测
      if (!controller || controller.disableRay) continue;
      // 优先检测UI对象
      if (controller._hoverType && controller._hoverType === "UI") continue;

      const intersects = this._findBestIntersection(controller);

      this._handleIntersectionResult(intersects, controller, i);
    }
  }

  // 射线检测优化方法
  _findBestIntersection(controller) {
    const raycaster = this.raycasters.get(controller);
    const direction = RayUtils.getControllerDirection(
      controller,
      this.tempMatrix
    );

    raycaster.set(controller.getWorldPosition(new Vector3()), direction);

    return this.queries.intersectable.results.reduce((best, entity) => {
      // 增加组件类型验证，避免UI对象也被检测到（Ecsy插件的bug）
      if (!entity.hasComponent(ObjectIntersectableCmp)) return best;

      const object = entity.getComponent(Object3DCmp)?.object;
      if (!object) return best;
      if (!this._isValidObject(object)) {
        return best;
      }

      const intersect = raycaster.intersectObject(object, false)[0];
      return this._compareIntersections(best, intersect);
    }, null);
  }

  _compareIntersections(currentBest, newIntersect) {
    if (!newIntersect) return currentBest;
    if (!currentBest) return newIntersect;

    // 优先级比较逻辑
    const currentPriority = currentBest.object._priority || 0;
    const newPriority = newIntersect.object._priority || 0;

    if (newPriority < currentPriority) {
      return newIntersect;
    } else if (newPriority === currentPriority) {
      // 相同优先级时选择距离更近的
      return newIntersect.distance <= currentBest.distance
        ? newIntersect
        : currentBest;
    }
    return currentBest;
  }

  // 对象有效性检查
  _isValidObject(object) {
    return (
      object?.visible &&
      object?.material?.visible &&
      object?.parent?.visible &&
      !object._dontCheckEcsy
    );
  }

  _handleIntersectionResult(intersect, controller, index) {
    if (!intersect) {
      this._cancelSelect(controller, index);
      if (controller._cursor && controller._cursor)
        controller._cursor.removeFromParent();
      if (controller._floorCursor && controller._floorCursor.parent)
        controller._floorCursor.removeFromParent();
    } else {
      this._select(intersect.object, controller, index);
      if (controller._cursor) {
        if (controller._cursor._parent)
          controller._cursor._parent.add(controller._cursor);
        let normal = intersect.face.normal;
        let normalMatrix = new Matrix3();
        let worldNormal = new Vector3();
        normalMatrix.getNormalMatrix(intersect.object.matrixWorld);
        worldNormal.copy(normal).applyMatrix3(normalMatrix).normalize();

        let pos = intersect.point.clone();

        controller.updateMatrixWorld();
        let localPos = pos
          .clone()
          .applyMatrix4(controller.matrixWorld.clone().invert());
        let dis = localPos.distanceTo(new Vector3());
        controller.children[0].scale.z = dis;

        if (
          intersect.object._meshtag &&
          intersect.object._meshtag === "floorCollider" &&
          this.checkFloor
        ) {
          if (controller._floorCursor) {
            controller._floorCursor._parent.add(controller._floorCursor);
            controller._floorCursor.position.copy(pos);
            controller._floorCursor.lookAt(pos.clone().add(worldNormal));
            controller._floorCursor.position.add(
              worldNormal.multiplyScalar(-0.02)
            );
          }
          if (controller._cursor) {
            controller._cursor.removeFromParent();
          }
        } else {
          if (controller._floorCursor) {
            controller._floorCursor.removeFromParent();
          }
          if (controller._cursor) {
            controller._cursor._parent.add(controller._cursor);
            controller._cursor.position.copy(pos);
            controller._cursor.lookAt(pos.clone().add(worldNormal));
            //controller._cursor.position.add(worldNormal.multiplyScalar(0.02));
          }
        }
      }
    }
  }

  _select(object, controller, index) {
    if (this.lastObj[index] && object === this.lastObj[index]) return;

    this._cancelSelect(controller);

    if (object.onEcsyHoverEnter) {
      object.onEcsyHoverEnter(object);
    }

    controller._hoverType = "Object";
    this.lastObj[index] = object;
  }

  _cancelSelect(controller, index) {
    if (this.lastObj[index]) {
      if (this.lastObj[index].onEcsyHoverExit) {
        this.lastObj[index].onEcsyHoverExit(this.lastObj[index]);
      }
    }
    controller._hoverType = null;
    this.lastObj[index] = null;
    controller.children[0].scale.z = 1.5;
  }
}

ObjectHandRaySystem.queries = {
  intersectable: {
    components: [ObjectIntersectableCmp],
  },
};
